/** @format */

import {createTheme} from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface CustomColor {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
  }

  interface SemanticColor {
    main: string;
    hover: string;
    active: string;
    on: string;
    container: string;
    hoverContainer: string;
    activeContainer: string;
    onContainer: string;
  }

  interface SurfaceColor {
    background: string;
    surface1: string;
    surface2: string;
    surface3: string;
    onSurface: string;
    onSurfaceLight: string;
    onSurfaceMuted: string;
    inverseSurface: string;
    inverseOnSurface: string;
  }

  interface DisabledColor {
    disabledLow: string;
    disabled: string;
    disabledHigh: string;
    inverseDisabledLow: string;
    inverseDisabled: string;
    inverseDisabledHigh: string;
  }

  interface Palette {
    figma_gray_light_0?: CustomColor;
    figma_gray_light_1?: CustomColor;
    figma_gray_light_2?: CustomColor;
    figma_gray_light_3?: CustomColor;
    figma_gray_light_4?: CustomColor;
    figma_gray_middle_dark?: CustomColor;
    figma_gray_middle_light?: CustomColor;
    figma_gray_middle?: CustomColor;
    figma_gray_dark_0?: CustomColor;
    figma_gray_dark_1?: CustomColor;
    figma_gray_dark_2?: CustomColor;
    figma_gray_dark_3?: CustomColor;
    figma_gray_dark_4?: CustomColor;
    figma_gray_dark_5?: CustomColor;
    figma_gray_dark?: CustomColor;
    figma_red_1?: CustomColor;
    figma_red_2?: CustomColor;
    figma_red_3?: CustomColor;
    figma_red_4?: CustomColor;
    figma_red_5?: CustomColor;
    figma_brown_1?: CustomColor;
    figma_brown_2?: CustomColor;
    figma_blue_1?: CustomColor;
    figma_blue_2?: CustomColor;
    figma_blue_3?: CustomColor;
    figma_yellow?: CustomColor;
    figma_yellow_alt?: CustomColor;
    error_secondary?: CustomColor;
    figma_primary?: CustomColor;
    figma_emerald?: CustomColor;
    figma_surface?: CustomColor;
    figma_surface_muted?: CustomColor;
    figma_disabled?: CustomColor;
    figma_good?: CustomColor;

    // New semantic palette
    semantic_dark?: {
      primary: SemanticColor;
      secondary: SemanticColor;
      tertiary: SemanticColor;
      bad: SemanticColor;
      good: SemanticColor;
      alert: SemanticColor;
      surface: SurfaceColor;
      disabled: DisabledColor;
    };
    semantic_light?: {
      primary: SemanticColor;
      secondary: SemanticColor;
      tertiary: SemanticColor;
      bad: SemanticColor;
      good: SemanticColor;
      alert: SemanticColor;
      surface: SurfaceColor;
      disabled: DisabledColor;
    };
  }
}

declare module '@mui/material/styles/createTheme' {
  interface Theme {
    custom_typography: {
      link: Record<string, unknown>;
    };

    backdrop: {
      main: string;
    };
  }
}

const baseTheme = createTheme({
  spacing: 4,
  // https://material-ui.com/customization/palette/#dark-mode
  palette: {
    mode: 'dark',
    background: {
      // This sets <body> background color
      default: '#010202',
    },
    error: {
      // Error text
      main: '#EE4D50',
    },
    warning: {
      main: '#DDA14D',
    },
    success: {
      main: '#749F23',
    },
    primary: {
      // Primary button background
      main: '#F7F8FA',
    },
    secondary: {
      main: '#FFFFFF',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#141414',
    },
    action: {
      // Button border by default
      active: '#4D4E4E',
      // Button border on hover
      hover: '#808080',
      // Button border on focus
      selected: '#999A9A',
      // Disabled button text
      disabled: '#606161',
      // Disabled button background
      disabledBackground: '#202020',
    },
  },
});

const theme = createTheme(baseTheme, {
  backdrop: {
    main: 'rgba(10, 10, 10, 0.9)',
  },
  custom_typography: {
    // https://github.com/mui-org/material-ui/issues/22257#issuecomment-782557096
    link: {
      color: '#A6A6A6',
      fontSize: 14,
      fontWeight: '700',
      lineHeight: '20px',
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
        textDecorationColor: '#A6A6A6',
      },
    },
  },
  typography: {
    h1: {
      fontSize: 28,
      fontWeight: '700',
      lineHeight: '32px',
      whiteSpace: 'pre-line',
    },
    h2: {
      fontSize: 18,
      fontWeight: '700',
      lineHeight: '22px',
      whiteSpace: 'pre-line',
    },
    body1: {
      fontSize: 16,
      fontWeight: '400',
      lineHeight: '20px',
      whiteSpace: 'pre-line',
    },
    body2: {
      fontSize: 14,
      fontWeight: '400',
      lineHeight: '16px',
      whiteSpace: 'pre-line',
    },
  },
  // https://material-ui.com/customization/components/#global-theme-override
  components: {
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchHeader: {
          color: '#444444',
        },
      },
    },
    MuiPickersBasePicker: {
      styleOverrides: {
        pickerView: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        day: {
          backgroundColor: '#FFFFFF',
          color: '#444444',
        },
        daySelected: {
          backgroundColor: '#444444',
          color: '#AAAAAA',
        },
        dayDisabled: {
          color: '#AAAAAA',
        },
        current: {
          backgroundColor: '#FF8888',
          color: '#444444',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: 16,
          fontWeight: '500',
          lineHeight: '20px',
          textTransform: 'none',
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            boxShadow: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
        containedPrimary: {
          color: baseTheme.palette.text.secondary,
          '&:hover': {
            backgroundColor: '#E6E6E6',
          },
        },
        containedSizeLarge: {
          fontSize: 16,
          padding: 22,
        },
        outlinedPrimary: {
          borderColor: baseTheme.palette.action.active,
          // palette.primary.main is used by default
          // (since outlined button is inverted contained one)
          color: baseTheme.palette.text.primary,
          '&:hover': {
            backgroundColor: baseTheme.palette.background.default,
            borderColor: baseTheme.palette.action.hover,
          },
        },
        outlinedSizeLarge: {
          fontSize: 16,
          padding: 22,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.primary.main,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 0,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: baseTheme.palette.text.primary,
            opacity: 0.5,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-disabled': {
            '-webkit-text-fill-color': baseTheme.palette.text.secondary,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: baseTheme.palette.background.default,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
          paddingTop: 0,
        },
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          lineHeight: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
        },
        paper: {
          backgroundColor: '#4A4A4A',
          borderRadius: 8,
          '&:hover': {
            backgroundColor: '#4A4A4A',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: '#424242',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingBottom: baseTheme.spacing(4),
          paddingLeft: baseTheme.spacing(6),
          paddingRight: baseTheme.spacing(6),
          paddingTop: baseTheme.spacing(4),
          '&:hover': {
            backgroundColor: '#393939',
          },
          '&.Mui-selected': {
            backgroundColor: baseTheme.palette.secondary.main,
            color: baseTheme.palette.text.secondary,
            '&:hover': {
              backgroundColor: '#F0F0F0',
            },
          },
        },
      },
    },
    // Used in TextField
    MuiOutlinedInput: {
      styleOverrides: {
        // https://material-ui.com/components/text-fields/#customized-inputs
        root: {
          borderRadius: 8,
          fontSize: 14,
          fontWeight: '400',
          lineHeight: '16px',
          '&:hover fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.action.hover,
          },
          '&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.action.selected,
            borderWidth: 1,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#fff',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        noOptions: {
          color: baseTheme.palette.text.primary,
        },
      },
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiButton: {
      fullWidth: true,
      size: 'large',
    },
    MuiTextField: {
      fullWidth: true,
      required: true,
      variant: 'outlined',
    },
  },
  palette: {
    figma_gray_light_0: baseTheme.palette.augmentColor({
      color: {
        main: '#E3E3E3',
      },
      name: 'figma_gray_light_0',
    }),
    figma_gray_light_1: baseTheme.palette.augmentColor({
      color: {
        main: '#F1F1F1',
      },
      name: 'figma_gray_light_1',
    }),
    figma_gray_light_2: baseTheme.palette.augmentColor({
      color: {
        main: '#FAFAFA',
      },
      name: 'figma_gray_light_2',
    }),
    figma_gray_light_3: baseTheme.palette.augmentColor({
      color: {
        main: '#FCFCFC',
      },
      name: 'figma_gray_light_3',
    }),
    figma_gray_light_4: baseTheme.palette.augmentColor({
      color: {
        main: '#FAFBFE',
      },
      name: 'figma_gray_light_4',
    }),
    figma_gray_middle_dark: baseTheme.palette.augmentColor({
      color: {
        dark: '#696A6E',
        main: '#8B8B8B',
      },
      name: 'figma_gray_middle_dark',
    }),
    figma_gray_middle_light: baseTheme.palette.augmentColor({
      color: {
        main: '#DFDFDF',
      },
      name: 'figma_gray_middle_light',
    }),
    figma_gray_middle: baseTheme.palette.augmentColor({
      color: {
        main: '#DADADA',
      },
      name: 'figma_gray_middle',
    }),
    figma_gray_dark_0: baseTheme.palette.augmentColor({
      color: {
        main: '#111111',
      },
      name: 'figma_gray_dark_0',
    }),
    figma_gray_dark_1: baseTheme.palette.augmentColor({
      color: {
        main: '#202020',
      },
      name: 'figma_gray_dark_1',
    }),
    figma_gray_dark_2: baseTheme.palette.augmentColor({
      color: {
        main: '#2B2B2B',
      },
      name: 'figma_gray_dark_2',
    }),
    figma_gray_dark_3: baseTheme.palette.augmentColor({
      color: {
        main: '#383838',
      },
      name: 'figma_gray_dark_3',
    }),
    figma_gray_dark_4: baseTheme.palette.augmentColor({
      color: {
        main: '#4A4B4F',
      },
      name: 'figma_gray_dark_4',
    }),
    figma_gray_dark_5: baseTheme.palette.augmentColor({
      color: {
        main: '#141418',
      },
      name: 'figma_gray_dark_5',
    }),
    figma_gray_dark: baseTheme.palette.augmentColor({
      color: {
        main: '#1A1A1A',
      },
      name: 'figma_gray_dark',
    }),
    figma_red_1: baseTheme.palette.augmentColor({
      color: {
        main: '#CE5959',
      },
      name: 'figma_red_1',
    }),
    figma_red_2: baseTheme.palette.augmentColor({
      color: {
        main: '#FF6164',
      },
      name: 'figma_red_2',
    }),
    figma_red_3: baseTheme.palette.augmentColor({
      color: {
        main: '#AE5658',
      },
      name: 'figma_red_3',
    }),
    figma_red_4: baseTheme.palette.augmentColor({
      color: {
        main: '#BE4E50',
      },
      name: 'figma_red_4',
    }),
    figma_red_5: baseTheme.palette.augmentColor({
      color: {
        main: '#FF4D4F',
      },
      name: 'figma_red_5',
    }),
    figma_brown_1: baseTheme.palette.augmentColor({
      color: {
        main: '#EFAE54',
      },
      name: 'figma_brown_1',
    }),
    figma_brown_2: baseTheme.palette.augmentColor({
      color: {
        main: '#FFBF67',
      },
      name: 'figma_brown_2',
    }),
    figma_blue_1: baseTheme.palette.augmentColor({
      color: {
        main: '#6288EF',
      },
      name: 'figma_blue_1',
    }),
    figma_blue_2: baseTheme.palette.augmentColor({
      color: {
        main: '#779BFD',
      },
      name: 'figma_blue_2',
    }),
    figma_blue_3: baseTheme.palette.augmentColor({
      color: {
        main: '#547AE0',
      },
      name: 'figma_blue_3',
    }),
    figma_yellow: baseTheme.palette.augmentColor({
      color: {
        main: '#AA8258',
      },
      name: 'figma_yellow',
    }),
    figma_yellow_alt: baseTheme.palette.augmentColor({
      color: {
        main: '#C0B059',
      },
      name: 'figma_yellow_alt',
    }),
    error_secondary: baseTheme.palette.augmentColor({
      color: {
        main: '#EA5759',
      },
      name: 'error_secondary',
    }),
    figma_primary: baseTheme.palette.augmentColor({
      color: {
        dark: '#3E88C5',
        main: '#58ACF2',
        light: '#83C0F5',
      },
      name: 'figma_primary',
    }),
    figma_emerald: baseTheme.palette.augmentColor({
      color: {
        main: '#419466',
      },
      name: 'figma_emerald',
    }),
    figma_surface: baseTheme.palette.augmentColor({
      color: {
        main: '#2D2E32',
        light: '#CDCED4',
      },
      name: 'figma_surface',
    }),
    figma_surface_muted: baseTheme.palette.augmentColor({
      color: {
        main: '#838489',
      },
      name: 'figma_surface_muted',
    }),
    figma_disabled: baseTheme.palette.augmentColor({
      color: {
        main: '#7e7f81',
      },
      name: 'figma_disabled',
    }),
    figma_good: baseTheme.palette.augmentColor({
      color: {
        main: '#F9FCFE',
      },
      name: 'figma_good',
    }),

    // Новая семантическая палитра для тёмной темы
    semantic_dark: {
      primary: {
        main: '#3E88C5',
        hover: '#58ACF2',
        active: '#83C8F5',
        on: '#F9FCFE',
        container: '#214D72',
        hoverContainer: '#316E9F',
        activeContainer: '#3E88C5',
        onContainer: '#F9FCFE',
      },
      secondary: {
        main: '#419466',
        hover: '#5A9B83',
        active: '#86CAA0',
        on: '#F9FCFA',
        container: '#225438',
        hoverContainer: '#337751',
        activeContainer: '#419466',
        onContainer: '#F9FCFA',
      },
      tertiary: {
        main: '#B17629',
        hover: '#DB9640',
        active: '#E4B073',
        on: '#FDFBF8',
        container: '#664214',
        hoverContainer: '#8E5E1F',
        activeContainer: '#B17629',
        onContainer: '#FDFBF8',
      },
      bad: {
        main: '#C06868',
        hover: '#EC8786',
        active: '#F2A4A2',
        on: '#FEFAFA',
        container: '#6F3A3A',
        hoverContainer: '#9B5353',
        activeContainer: '#C06868',
        onContainer: '#FEFAFA',
      },
      good: {
        main: '#419466',
        hover: '#5AB983',
        active: '#86CAA0',
        on: '#F9FCFA',
        container: '#225438',
        hoverContainer: '#337751',
        activeContainer: '#419466',
        onContainer: '#F9FCFA',
      },
      alert: {
        main: '#B17629',
        hover: '#DB9640',
        active: '#E4B073',
        on: '#FDFBF8',
        container: '#664214',
        hoverContainer: '#8E5E1F',
        activeContainer: '#B17629',
        onContainer: '#FDFBF8',
      },
      surface: {
        background: '#141418',
        surface1: '#2D2E32',
        surface2: '#4A4B4F',
        surface3: '#696A6E',
        onSurface: '#FAFBFE',
        onSurfaceLight: '#DCDEDC',
        onSurfaceMuted: '#838489',
        inverseSurface: '#FAFBFE',
        inverseOnSurface: '#141418',
      },
      disabled: {
        disabledLow: '#4A4B4F',
        disabled: '#696A6E',
        disabledHigh: '#838489',
        inverseDisabledLow: '#E2E3E9',
        inverseDisabled: '#A5A6AB',
        inverseDisabledHigh: '#838489',
      },
    },
    // Новая семантическая палитра для светлой темы
    semantic_light: {
      primary: {
        main: '#3E88C5',
        hover: '#58ACF2',
        active: '#83C8F5',
        on: '#F9FCFE',
        container: '#D0E7FB',
        hoverContainer: '#ECF5FD',
        activeContainer: '#F9FCFE',
        onContainer: '#051522',
      },
      secondary: {
        main: '#419466',
        hover: '#5A9B83',
        active: '#86CAA0',
        on: '#F9FCFA',
        container: '#D1EADA',
        hoverContainer: '#ECF6EF',
        activeContainer: '#F9FCFA',
        onContainer: '#06170D',
      },
      tertiary: {
        main: '#B17629',
        hover: '#DB9640',
        active: '#E4B073',
        on: '#FDFBF8',
        container: '#F4E1CA',
        hoverContainer: '#FBF2E9',
        activeContainer: '#FDFBF8',
        onContainer: '#1E1103',
      },
      bad: {
        main: '#C06868',
        hover: '#EC8786',
        active: '#F2A4A2',
        on: '#FEFAFA',
        container: '#FADCCA',
        hoverContainer: '#FDF0F0',
        activeContainer: '#FEFAFA',
        onContainer: '#210D0D',
      },
      good: {
        main: '#419466',
        hover: '#5A9B83',
        active: '#86CAA0',
        on: '#F9FCFA',
        container: '#D1EADA',
        hoverContainer: '#ECF6EF',
        activeContainer: '#F9FCFA',
        onContainer: '#06170D',
      },
      alert: {
        main: '#B17629',
        hover: '#DB9640',
        active: '#E4B073',
        on: '#FDFBF8',
        container: '#F4E1CA',
        hoverContainer: '#FBF2E9',
        activeContainer: '#FDFBF8',
        onContainer: '#1E1103',
      },
      surface: {
        background: '#FAFBFE',
        surface1: '#F2F3F8',
        surface2: '#E2E3E9',
        surface3: '#DCDEDC',
        onSurface: '#141418',
        onSurfaceLight: '#696A6E',
        onSurfaceMuted: '#B9BABF',
        inverseSurface: '#141418',
        inverseOnSurface: '#FAFBFE',
      },
      disabled: {
        disabledLow: '#E2E3E9',
        disabled: '#A5A6AB',
        disabledHigh: '#838489',
        inverseDisabledLow: '#4A4B4F',
        inverseDisabled: '#696A6E',
        inverseDisabledHigh: '#838489',
      },
    },
  },
});

export default theme;
